// src/pages/FormFill.jsx

import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import "../index.css";

const API_BASE = "https://textshare-node.rupioo.com";
const PURPLE = "#673ab7";

/* =========================================================
   FORM FILL PAGE
========================================================= */

export default function FormFill() {
  const { id } = useParams();

  const [form, setForm] = useState(null);
  const [answers, setAnswers] = useState({});

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const [userLocation, setUserLocation] = useState(null);
  const [locationStatus, setLocationStatus] = useState("");

  /* =========================================================
     LOAD FORM
  ========================================================= */

  useEffect(() => {
    let alive = true;

    async function loadForm() {
      setLoading(true);
      setError("");
      setLocationStatus("");

      try {
        let location = null;

        /*
         * First check whether this form requires location.
         *
         * If your backend does not have /access,
         * you can remove this first request and load /public directly.
         */
        try {
          const accessResponse = await fetch(`${API_BASE}/forms/${id}/access`);

          if (accessResponse.ok) {
            const accessData = await accessResponse.json();

            if (accessData?.requiresLocation) {
              setLocationStatus("Checking your location…");

              location = await getBrowserLocation();

              if (!alive) return;

              setUserLocation(location);

              const verifyUrl = new URL(`${API_BASE}/forms/${id}/access`);

              verifyUrl.searchParams.set("latitude", String(location.latitude));

              verifyUrl.searchParams.set(
                "longitude",
                String(location.longitude),
              );

              const verifyResponse = await fetch(verifyUrl.toString());

              const verifyData = await verifyResponse.json().catch(() => ({}));

              if (!verifyResponse.ok || verifyData?.allowed === false) {
                throw new Error(
                  verifyData?.message ||
                    "You are outside the allowed area for this form.",
                );
              }

              setLocationStatus("Location verified");
            }
          }
        } catch (locationError) {
          /*
           * If this was an actual geo-fence error, throw it.
           */
          if (
            locationError?.message?.toLowerCase().includes("location") ||
            locationError?.message?.toLowerCase().includes("outside")
          ) {
            throw locationError;
          }
        }

        /*
         * Load public form
         */
        const formUrl = new URL(`${API_BASE}/forms/${id}/public`);

        if (location) {
          formUrl.searchParams.set("latitude", String(location.latitude));

          formUrl.searchParams.set("longitude", String(location.longitude));
        }

        const response = await fetch(formUrl.toString());

        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(
            data?.message || `Failed to load form (${response.status})`,
          );
        }

        if (!alive) return;

        setForm(data);

        /*
         * Initialize answers.
         *
         * Payment questions do not need user input.
         * We internally set them as "payment_pending"
         * so an older backend validator does not reject
         * a required payment question.
         */
        const initialAnswers = {};

        (data.questions || []).forEach((q) => {
          if (q.type === "checkboxes") {
            initialAnswers[q.id] = [];
          } else if (q.type === "payment") {
            initialAnswers[q.id] = "payment_pending";
          } else {
            initialAnswers[q.id] = "";
          }
        });

        setAnswers(initialAnswers);
      } catch (e) {
        if (alive) {
          setError(e?.message || "Failed to load form.");
        }
      } finally {
        if (alive) {
          setLoading(false);
        }
      }
    }

    loadForm();

    return () => {
      alive = false;
    };
  }, [id]);

  /* =========================================================
     ANSWER HELPERS
  ========================================================= */

  const onChange = (questionId, value) => {
    setAnswers((previous) => ({
      ...previous,
      [questionId]: value,
    }));
  };

  const onCheckboxToggle = (questionId, option) => {
    setAnswers((previous) => {
      const current = Array.isArray(previous[questionId])
        ? previous[questionId]
        : [];

      const exists = current.includes(option);

      return {
        ...previous,
        [questionId]: exists
          ? current.filter((item) => item !== option)
          : [...current, option],
      };
    });
  };

  /* =========================================================
     PAYMENT QUESTIONS
  ========================================================= */

  const paymentQuestions = useMemo(() => {
    return (form?.questions || []).filter(
      (question) => question.type === "payment",
    );
  }, [form]);

  const hasPayment = paymentQuestions.length > 0;

  /*
   * If the form contains a payment-link payment question,
   * the response is saved first and then the user is redirected
   * directly to that payment link.
   */
  const paymentLink = useMemo(() => {
    const linkQuestion = paymentQuestions.find(
      (question) =>
        question?.paymentConfig?.method === "payment_link" &&
        isValidHttpUrl(question?.paymentConfig?.paymentUrl),
    );

    return linkQuestion?.paymentConfig?.paymentUrl || "";
  }, [paymentQuestions]);

  /* =========================================================
     VALIDATION
  ========================================================= */

  const canSubmit = useMemo(() => {
    if (!form) return false;

    for (const question of form.questions || []) {
      /*
       * Payment requires no user input.
       */
      if (question.type === "payment") {
        continue;
      }

      if (!question.required) {
        continue;
      }

      const value = answers[question.id];

      if (question.type === "checkboxes") {
        if (!Array.isArray(value) || value.length === 0) {
          return false;
        }
      } else {
        if (
          value === null ||
          value === undefined ||
          String(value).trim() === ""
        ) {
          return false;
        }
      }
    }

    return true;
  }, [form, answers]);

  /* =========================================================
     SUBMIT
  ========================================================= */
  console.log(API_BASE);
  // console.log(form.thumbnailUrl);

  const submit = async () => {
    if (!form || submitting) return;

    if (!canSubmit) {
      setError("Please complete all required questions.");
      return;
    }

    setSubmitting(true);
    setError("");

    try {
      /*
       * Response gets SAVED FIRST.
       */
      const payload = {
        answers: (form.questions || []).map((question) => ({
          questionId: question.id,

          /*
           * No transaction reference is required.
           */
          value:
            question.type === "payment"
              ? "payment_pending"
              : answers[question.id],
        })),

        /*
         * Send location again so backend can verify
         * geo-fence during submission.
         */
        location: userLocation
          ? {
              latitude: userLocation.latitude,
              longitude: userLocation.longitude,
              accuracy: userLocation.accuracy ?? null,
            }
          : undefined,
      };

      const response = await fetch(
        `${API_BASE}/forms/${form.id || id}/responses`,
        {
          method: "POST",

          headers: {
            "Content-Type": "application/json",
          },

          body: JSON.stringify(payload),
        },
      );

      const result = await response.json().catch(() => ({}));

      if (!response.ok) {
        if (Array.isArray(result?.errors) && result.errors.length) {
          throw new Error(
            result.errors
              .map((item) => item?.message || String(item))
              .join("\n"),
          );
        }

        throw new Error(
          result?.message || `Submit failed (${response.status})`,
        );
      }

      /*
       * RESPONSE HAS NOW BEEN SAVED.
       *
       * PAYMENT LINK:
       * Show only the requested success dialog and then
       * redirect directly to the configured payment link.
       */
      if (paymentLink) {
        // alert("Your form response was submitted");
        window.location.replace(paymentLink);
        return;
      }

      /*
       * OTHER PAYMENT TYPES:
       * Keep the existing payment-instructions flow for
       * QR code, bank transfer, UPI, instructions, etc.
       */
      if (hasPayment) {
        const paymentData = paymentQuestions.map((question) => ({
          id: question.id,
          title: question.label || "Payment",
          paymentConfig: question.paymentConfig || {},
        }));

        sessionStorage.setItem(
          "pendingPaymentInstructions",
          JSON.stringify({
            formId: form.id || id,
            formTitle: form.title || "",
            payments: paymentData,
          }),
        );

        window.location.replace("/?showPayment=true");

        return;
      }

      /*
       * Normal form without payment.
       */
      alert("Your form response was submitted");

      window.location.replace("/");
    } catch (e) {
      setError(e?.message || "Failed to submit form.");
    } finally {
      setSubmitting(false);
    }
  };

  /* =========================================================
     LOADING
  ========================================================= */
  // console.log(form.thumbnailUrl);
  if (loading) {
    return (
      <>
        <FormStyles />

        <div className="gf-page">
          <div className="gf-loading">
            <div className="gf-spinner" />

            <p>{locationStatus || "Loading form…"}</p>
          </div>
        </div>
      </>
    );
  }

  /* =========================================================
     ERROR SCREEN
  ========================================================= */

  if (error && !form) {
    return (
      <>
        <FormStyles />

        <div className="gf-page">
          <div className="gf-container">
            <div className="gf-card gf-error-card">
              <h2>Unable to open form</h2>

              <p>{error}</p>

              <button
                className="gf-outline-btn"
                onClick={() => window.location.reload()}
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (!form) {
    return (
      <>
        <FormStyles />

        <div className="gf-page">
          <div className="gf-container">
            <div className="gf-card">
              <h2>Form not found</h2>
            </div>
          </div>
        </div>
      </>
    );
  }

  /* =========================================================
     FORM
  ========================================================= */

  const thumbnailSrc = form?.thumbnailUrl?.includes("/uploads/")
    ? `${API_BASE.replace(/\/+$/, "")}${form.thumbnailUrl.slice(
        form.thumbnailUrl.indexOf("/uploads/"),
      )}`
    : form?.thumbnailUrl || "";

  return (
    <>
      <FormStyles />

      <div className="gf-page">
        <div className="gf-container">
          {/* =========================
              FORM HEADER
          ========================= */}

          <div className="gf-header-card">
            <div className="gf-purple-bar" />

            {thumbnailSrc ? (
              <img
                src={thumbnailSrc}
                alt={form?.title || "Form thumbnail"}
                className="gf-thumbnail"
              />
            ) : null}

            <div className="gf-header-content">
              <h1>{form.title}</h1>

              {form.description ? <p>{form.description}</p> : null}

              <div className="gf-header-bottom">
                <span>
                  <span className="gf-required">*</span> Indicates required
                  question
                </span>

                {locationStatus ? (
                  <span className="gf-location">✓ {locationStatus}</span>
                ) : null}
              </div>
            </div>
          </div>

          {/* =========================
              ERROR
          ========================= */}

          {error ? <div className="gf-error">{error}</div> : null}

          {/* =========================
              QUESTIONS
          ========================= */}

          {(form.questions || []).map((question) => (
            <QuestionCard
              key={question.id}
              question={question}
              value={answers[question.id]}
              onChange={onChange}
              onCheckboxToggle={onCheckboxToggle}
            />
          ))}

          {/* =========================
              SUBMIT
          ========================= */}

          <div className="gf-submit-row">
            <button
              className="gf-submit-btn"
              disabled={!canSubmit || submitting}
              onClick={submit}
            >
              {submitting ? "Saving…" : hasPayment ? "Save and Pay" : "Submit"}
            </button>

            {!canSubmit ? (
              <span className="gf-required-message">
                Please complete required questions.
              </span>
            ) : null}
          </div>

          <div className="gf-footer">
            Never submit sensitive passwords through online forms.
          </div>
        </div>
      </div>
    </>
  );
}

/* =========================================================
   QUESTION CARD
========================================================= */

function QuestionCard({ question, value, onChange, onCheckboxToggle }) {
  /*
   * Payment question:
   *
   * We only show a small notice.
   * Actual instructions will appear AFTER the response
   * is saved and redirected to "/".
   */
  if (question.type === "payment") {
    return (
      <div className="gf-card">
        <div className="gf-question-title">
          {question.label || "Payment"}

          {question.required ? <span className="gf-required">*</span> : null}
        </div>

        <div className="gf-payment-notice">
          {question?.paymentConfig?.method === "payment_link"
            ? "After submitting the form, you will be redirected to the payment link."
            : "Payment instructions will be shown after you save this form."}
        </div>
      </div>
    );
  }

  return (
    <div className="gf-card">
      <div className="gf-question-title">
        {question.label}

        {question.required ? <span className="gf-required">*</span> : null}
      </div>

      {/* SHORT TEXT */}

      {question.type === "short_text" && (
        <input
          className="gf-text-input"
          type="text"
          placeholder="Your answer"
          value={value || ""}
          onChange={(event) => onChange(question.id, event.target.value)}
        />
      )}

      {/* PARAGRAPH */}

      {question.type === "paragraph" && (
        <textarea
          className="gf-text-input gf-textarea"
          rows={3}
          placeholder="Your answer"
          value={value || ""}
          onChange={(event) => onChange(question.id, event.target.value)}
        />
      )}

      {/* MULTIPLE CHOICE */}

      {question.type === "multiple_choice" && (
        <div className="gf-options">
          {(question.options || []).map((option, index) => (
            <label className="gf-option" key={index}>
              <input
                type="radio"
                name={`question-${question.id}`}
                checked={value === option}
                onChange={() => onChange(question.id, option)}
              />

              <span>{option}</span>
            </label>
          ))}
        </div>
      )}

      {/* CHECKBOXES */}

      {question.type === "checkboxes" && (
        <div className="gf-options">
          {(question.options || []).map((option, index) => (
            <label className="gf-option" key={index}>
              <input
                type="checkbox"
                checked={Array.isArray(value) && value.includes(option)}
                onChange={() => onCheckboxToggle(question.id, option)}
              />

              <span>{option}</span>
            </label>
          ))}
        </div>
      )}

      {/* DROPDOWN */}

      {question.type === "dropdown" && (
        <select
          className="gf-select"
          value={value || ""}
          onChange={(event) => onChange(question.id, event.target.value)}
        >
          <option value="">Choose</option>

          {(question.options || []).map((option, index) => (
            <option key={index} value={option}>
              {option}
            </option>
          ))}
        </select>
      )}

      {/* DATE */}

      {question.type === "date" && (
        <input
          className="gf-date-input"
          type="date"
          value={value || ""}
          onChange={(event) => onChange(question.id, event.target.value)}
        />
      )}

      {/* NUMBER */}

      {question.type === "number" && (
        <input
          className="gf-text-input"
          type="number"
          min={question.min ?? undefined}
          max={question.max ?? undefined}
          step={question.step ?? 1}
          placeholder="Your answer"
          value={value ?? ""}
          onChange={(event) => onChange(question.id, event.target.value)}
        />
      )}

      {/* RATING */}

      {question.type === "rating" && (
        <RatingInput question={question} value={value} onChange={onChange} />
      )}
    </div>
  );
}

/* =========================================================
   RATING
========================================================= */

function RatingInput({ question, value, onChange }) {
  const min = Number(question.min ?? 1);
  const max = Number(question.max ?? 5);

  const options = [];

  for (let i = min; i <= max && options.length < 10; i++) {
    options.push(i);
  }

  return (
    <div className="gf-rating">
      {options.map((number) => (
        <label key={number}>
          <input
            type="radio"
            name={`rating-${question.id}`}
            checked={Number(value) === number}
            onChange={() => onChange(question.id, String(number))}
          />

          <span>{number}</span>
        </label>
      ))}
    </div>
  );
}

/* =========================================================
   URL VALIDATION
========================================================= */

function isValidHttpUrl(value) {
  if (!value) return false;

  try {
    const url = new URL(value);

    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
}

/* =========================================================
   LOCATION
========================================================= */

function getBrowserLocation() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("Your browser does not support location services."));

      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
        });
      },

      (error) => {
        if (error.code === 1) {
          reject(
            new Error("Location permission is required to open this form."),
          );
        } else if (error.code === 2) {
          reject(new Error("Your current location could not be determined."));
        } else if (error.code === 3) {
          reject(new Error("Location request timed out. Please try again."));
        } else {
          reject(new Error("Unable to access your current location."));
        }
      },

      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0,
      },
    );
  });
}

/* =========================================================
   SIMPLE GOOGLE-FORMS-LIKE STYLES
========================================================= */

function FormStyles() {
  return (
    <style>{`
      * {
        box-sizing: border-box;
      }

      body {
        margin: 0;
        font-family:
          Arial,
          Helvetica,
          sans-serif;
      }

      .gf-page {
        min-height: 100vh;
        background: #f0ebf8;
        color: #202124;
        padding: 12px 0 50px;
      }

      .gf-container {
        width: calc(100% - 24px);
        max-width: 640px;
        margin: 0 auto;
      }

      /* =========================
         HEADER
      ========================= */

      .gf-header-card,
      .gf-card {
        background: white;
        border: 1px solid #dadce0;
        border-radius: 8px;
        margin-bottom: 12px;
        overflow: hidden;
      }

      .gf-purple-bar {
        height: 10px;
        background: ${PURPLE};
      }

      .gf-thumbnail {
        width: 100%;
        max-height: 240px;
        object-fit: cover;
        display: block;
      }

      .gf-header-content {
        padding: 22px 24px;
      }

      .gf-header-content h1 {
        margin: 0;
        font-size: 30px;
        line-height: 1.25;
        font-weight: 400;
      }

      .gf-header-content > p {
        margin: 12px 0 0;
        font-size: 14px;
        line-height: 1.5;
        white-space: pre-wrap;
      }

      .gf-header-bottom {
        border-top: 1px solid #dadce0;
        margin-top: 20px;
        padding-top: 12px;
        display: flex;
        justify-content: space-between;
        gap: 10px;
        flex-wrap: wrap;
        font-size: 12px;
        color: #5f6368;
      }

      .gf-required {
        color: #d93025;
        margin-left: 3px;
      }

      .gf-location {
        color: #188038;
      }

      /* =========================
         QUESTION CARD
      ========================= */

      .gf-card {
        padding: 24px;
      }

      .gf-question-title {
        font-size: 16px;
        line-height: 1.5;
        margin-bottom: 20px;
      }

      /* =========================
         TEXT INPUTS
      ========================= */

      .gf-text-input {
        width: 60%;
        max-width: 400px;
        min-width: 220px;
        border: none;
        border-bottom: 1px solid #dadce0;
        padding: 9px 0;
        outline: none;
        font-size: 14px;
        background: transparent;
        color: #202124;
        transition: border-color .15s;
      }

      .gf-text-input:focus {
        border-bottom: 2px solid ${PURPLE};
      }

      .gf-textarea {
        width: 100%;
        max-width: none;
        resize: vertical;
      }

      .gf-date-input {
        border: none;
        border-bottom: 1px solid #dadce0;
        padding: 8px 0;
        outline: none;
        font-size: 14px;
      }

      /* =========================
         OPTIONS
      ========================= */

      .gf-options {
        display: flex;
        flex-direction: column;
        gap: 14px;
      }

      .gf-option {
        display: flex;
        align-items: center;
        gap: 10px;
        cursor: pointer;
        font-size: 14px;
      }

      .gf-option input {
        width: 18px;
        height: 18px;
        accent-color: ${PURPLE};
        cursor: pointer;
      }

      /* =========================
         DROPDOWN
      ========================= */

      .gf-select {
        min-width: 180px;
        padding: 10px;
        border: 1px solid #dadce0;
        border-radius: 4px;
        background: white;
        font-size: 14px;
        outline: none;
      }

      .gf-select:focus {
        border-color: ${PURPLE};
      }

      /* =========================
         RATING
      ========================= */

      .gf-rating {
        display: flex;
        flex-wrap: wrap;
        gap: 18px;
      }

      .gf-rating label {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 5px;
        cursor: pointer;
      }

      .gf-rating input {
        width: 18px;
        height: 18px;
        accent-color: ${PURPLE};
      }

      /* =========================
         PAYMENT NOTICE
      ========================= */

      .gf-payment-notice {
        padding: 12px;
        background: #f8f5fc;
        border-radius: 4px;
        color: #5f6368;
        font-size: 13px;
      }

      /* =========================
         SUBMIT
      ========================= */

      .gf-submit-row {
        display: flex;
        align-items: center;
        gap: 15px;
        margin-top: 16px;
        margin-bottom: 22px;
      }

      .gf-submit-btn {
        background: ${PURPLE};
        color: white;
        border: none;
        border-radius: 4px;
        min-width: 90px;
        height: 38px;
        padding: 0 22px;
        font-size: 14px;
        font-weight: 500;
        cursor: pointer;
        box-shadow:
          0 1px 2px
          rgba(60,64,67,.3);
      }

      .gf-submit-btn:hover:not(:disabled) {
        background: #5e35b1;
      }

      .gf-submit-btn:disabled {
        opacity: .5;
        cursor: not-allowed;
      }

      .gf-required-message {
        color: #d93025;
        font-size: 12px;
      }

      /* =========================
         ERROR
      ========================= */

      .gf-error {
        padding: 14px 18px;
        background: #fce8e6;
        color: #c5221f;
        border-radius: 8px;
        margin-bottom: 12px;
        font-size: 13px;
        white-space: pre-line;
      }

      .gf-error-card {
        margin-top: 50px;
        text-align: center;
      }

      .gf-error-card h2 {
        font-weight: 400;
      }

      .gf-error-card p {
        color: #5f6368;
        font-size: 14px;
      }

      .gf-outline-btn {
        border: 1px solid #dadce0;
        background: white;
        color: ${PURPLE};
        border-radius: 4px;
        padding: 9px 18px;
        cursor: pointer;
        margin-top: 10px;
      }

      /* =========================
         LOADING
      ========================= */

      .gf-loading {
        padding-top: 100px;
        text-align: center;
        color: #5f6368;
      }

      .gf-spinner {
        width: 32px;
        height: 32px;
        margin: 0 auto 14px;
        border-radius: 50%;
        border: 3px solid #d1c4e9;
        border-top-color: ${PURPLE};
        animation: gf-spin .7s linear infinite;
      }

      @keyframes gf-spin {
        to {
          transform: rotate(360deg);
        }
      }

      /* =========================
         FOOTER
      ========================= */

      .gf-footer {
        text-align: center;
        color: #70757a;
        font-size: 11px;
        padding: 10px 0 30px;
      }

      /* =========================
         MOBILE
      ========================= */

      @media (max-width: 600px) {
        .gf-page {
          padding-top: 8px;
        }

        .gf-container {
          width: calc(100% - 16px);
        }

        .gf-header-content {
          padding: 20px;
        }

        .gf-header-content h1 {
          font-size: 25px;
        }

        .gf-card {
          padding: 20px;
        }

        .gf-text-input {
          width: 100%;
          min-width: 0;
        }

        .gf-submit-row {
          align-items: flex-start;
          flex-direction: column;
        }
      }
    `}</style>
  );
}
